import React, { useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { Download, Search } from "react-feather";
import SingleSubmissionModal from "../../../../components/Modals/SingleSubmissionModal";
import ActionButtonForTables from "../../../../components/Table/ActionButtonForTables";
import CustomDataTableIcons from "../../../../components/Table/CustomDataTableIcons";
import DateTooltip from "../../../../components/Table/DateTooltip";
import Last14Chip from "../../../../components/Table/Last14Chip";
import SubmissionResultIndicator from "../../../../components/Table/SubmissionResultIndicator";
import TableTitle from "../../../../components/Table/TableTitle";
import DisplayScore from "../../../../components/UI/DisplayScore";
import { ENGAGEMENT_TYPES } from "../../../../storage/constants";
import { makeDefaultTableOptions } from "../../../../storage/helpers";
import { CustomDatatableV2 } from "../../styles";
import SubmissionTypeChip from "../../../../components/SubmissionTypeChip";

const getTableData = (subs, over100Subs, currentCompanyId) => {
  if (!subs) return [];

  if (over100Subs) {
    const sorted = subs.sort(
      (a, b) => new Date(b.created) - new Date(a.created),
    );
    const sliced = sorted.slice(0, 100);
    return sliced.map((submission) => ({
      id: submission.id,
      date: new Date(submission.created).toISOString(),
      teamMemberName: `${submission.first_name} ${submission.last_name}`,
      quiz: submission.quiz_name,
      brand:
        submission?.root_quiz_company_id === currentCompanyId
          ? null
          : submission?.root_quiz_company_name,
      type: submission.assigned ? "assigned" : "extra credit",
      location:
        submission?.active_team_members.length > 1
          ? "multiple locations"
          : submission.active_team_members?.[0]?.location_name || "none",
      score: `${submission.score}/${submission.questions}`,
      passed: submission.passed ? "Yes" : "No",
    }));
  }

  return subs.map((submission) => ({
    id: submission.id,
    date: new Date(submission.created).toISOString(),
    teamMemberName: `${submission.first_name} ${submission.last_name}`,
    quiz: submission.quiz_name,
    brand:
      submission?.root_quiz_company_id === currentCompanyId
        ? null
        : submission?.root_quiz_company_name,
    type: submission.assigned ? "assigned" : "extra credit",
    location:
      submission?.active_team_members.length > 1
        ? "multiple locations"
        : submission.active_team_members?.[0]?.location_name || "none",
    score: `${submission.score}/${submission.questions}`,
    passed: submission.passed ? "Yes" : "No",
  }));
};

const AllSubmissionsTable = ({
  submissions,
  engagementType,
  currentCompanyId,
}) => {
  const [submissionInfo, setSubmissionInfo] = useState(null);
  const isUnder600Px = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const over100Subs = submissions && submissions.length > 100;

  const options = makeDefaultTableOptions("submissions-last-14");

  options.sortOrder = {
    name: "date",
    direction: "desc",
  };
  options.rowsPerPage = 10;
  options.textLabels = {
    body: {
      noMatch: "No new submissions in the last 14 days",
    },
  };

  if (isUnder600Px) {
    options.rowsPerPageOptions = [];
    options.jumpToPage = false;
  }

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: false,
        display: false,
        download: false,
      },
    },
    {
      name: "date",
      label: "Date",
      options: {
        filter: true,
        sort: true,
        display: true,
        customBodyRender: (value) => (
          <span style={{ fontSize: 12 }}>
            <DateTooltip date={value} shouldLocalize displayYear />
          </span>
        ),
      },
    },
    {
      name: "teamMemberName",
      label: "Member",
      options: {
        filter: true,
        sort: true,
      },
    },

    {
      name: "location",
      label: "Location",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (["multiple locations", "none"].includes(value)) {
            return (
              <span style={{ fontSize: 11, color: "darkgrey" }}>*{value}</span>
            );
          }
          return <span>{value}</span>;
        },
      },
    },
    {
      name: "quiz",
      label: "Module",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "brand",
      label: "Vendor",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (val) => {
          if (!val) return <span style={{ color: "darkgray" }}>---/---</span>;

          return <span>{val}</span>;
        },
      },
    },
    {
      name: "type",
      label: "Type",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <SubmissionTypeChip
            small
            submissionType={
              value === "assigned"
                ? ENGAGEMENT_TYPES.ASSIGNED
                : ENGAGEMENT_TYPES.EXTRACREDIT
            }
          />
        ),
      },
    },
    {
      name: "score",
      label: "Score",
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            // <DisplayScoreV2
            //   value={value}
            //   didPass={tableMeta.rowData[8] === "Yes"}
            // />
            <DisplayScore value={value} />
          );
        },
      },
    },
    {
      name: "passed",
      label: "Result",
      options: {
        filter: true,
        sort: true,
        display: true,
        customBodyRender: (value) => (
          <SubmissionResultIndicator value={value} />
        ),
      },
    },
    {
      name: "",
      label: "",
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => (
          <ActionButtonForTables
            Icon={Search}
            title="view"
            handleClick={() => setSubmissionInfo({ id: tableMeta.rowData[0] })}
          />
        ),
      },
    },
  ];

  const tableData = useMemo(
    () => getTableData(submissions, over100Subs, currentCompanyId),
    [submissions, currentCompanyId],
  );

  return (
    <>
      <CustomDatatableV2
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 8,
            }}
          >
            <TableTitle
              Icon={Download}
              title="Recent Submissions"
              submissionType={engagementType}
            />
            <Last14Chip />
          </div>
        }
        data={tableData}
        options={options}
        columns={columns}
        components={{
          icons: CustomDataTableIcons,
        }}
      />
      <SingleSubmissionModal
        submissionInfo={submissionInfo}
        setSubmissionInfo={setSubmissionInfo}
      />
    </>
  );
};

export default AllSubmissionsTable;
